import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import {
  applyStyleRecipe,
  COLOR_PALETTES,
  PALETTE_GROUP_IDS,
  DESIGN_STYLES,
  FONT_PAIRS,
  googleFontSpecsFor,
  STYLE_PALETTE_PREFIX,
  CUSTOM_PALETTE_ROLES,
  SITE_MOTION_STYLES,
  type SiteMotionStyle,
  type DesignPalette,
  type DesignStyle,
  type CustomPalette,
  type ProjectTheme,
  type StyleRecipe,
} from '@webbuilder/theme'
import { IconCheck, IconChevronLeft, IconChevronRight, IconColorSwatch } from '@tabler/icons-react'
import { VelticaMascot } from '@webbuilder/ui'
import { useI18n } from '../../lib/i18n'
import { ColorPickerPopover } from '../ColorPickerPopover'

/**
 * El estilo del sitio en el asistente, un paso por decisión: los colores, la
 * letra y el estilo base (botones, tarjetas y aire), en ese orden. El estilo va
 * al final y no cambia lo elegido antes. Antes había además un paso de
 * «Detalles» con los botones, las tarjetas y el aire sueltos; lo cubre el
 * estilo.
 *
 * Todo es una receta (`StyleRecipe`) sobre el preset, igual que Style ›
 * Combinación del editor: lo que la IA eligió llega marcado y cada paso cambia
 * sólo lo suyo. Nada de esto llama a la IA; las muestras se dibujan con los
 * colores y la letra del sitio.
 */

type RecipePatch = { [K in keyof StyleRecipe]?: StyleRecipe[K] | undefined }

export type StyleStepProps = { recipe: StyleRecipe; theme: ProjectTheme; onChange: (next: StyleRecipe) => void }


function usePatch(recipe: StyleRecipe, onChange: (next: StyleRecipe) => void) {
  return (patch: RecipePatch) => {
    const next = { ...recipe, ...patch }
    for (const key of Object.keys(patch) as (keyof StyleRecipe)[]) if (patch[key] === undefined) delete next[key]
    onChange(next as StyleRecipe)
  }
}

/** Las familias de los estilos y las parejas, pedidas una vez: fuera del lienzo no están cargadas. */
function useWizardFonts() {
  useEffect(() => {
    if (typeof document === 'undefined' || document.querySelector('link[data-wizard-fonts]')) return
    const families = [
      ...FONT_PAIRS.flatMap((pair) => [pair.heading, pair.body]),
      ...DESIGN_STYLES.flatMap((style) => [style.fonts.heading, style.fonts.body]),
    ]
    const specs = googleFontSpecsFor([...new Set(families)].map((family) => `'${family}'`))
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.dataset.wizardFonts = 'true'
    link.href = `https://fonts.googleapis.com/css2?${specs.map((spec) => 'family=' + spec).join('&')}&display=swap`
    document.head.appendChild(link)
  }, [])
}

const fontStack = (family: string, serif: boolean, script = false) => `'${family}', ${script ? 'cursive' : serif ? 'serif' : 'sans-serif'}`

function Block({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <div className="dash-svc-setting-block cwa-wz-block">
      <label>{title}</label>
      {hint ? <span className="dash-svc-block-hint">{hint}</span> : null}
      {children}
    </div>
  )
}


/**
 * El estilo que se elige, con la paleta, la letra y el peso de los títulos
 * anclados: si todavía eran los del estilo de antes, se fijan a él para que no
 * cambien con éste. Es lo que se guarda al pulsar y lo que dibuja la muestra.
 */
function withStyle(recipe: StyleRecipe, styleId: string): StyleRecipe {
  const previous = DESIGN_STYLES.find((style) => style.id === recipe.designStyleId)
  return {
    ...recipe,
    designStyleId: styleId,
    ...(recipe.paletteId || !previous ? {} : { paletteId: STYLE_PALETTE_PREFIX + previous.id }),
    ...(recipe.fontPairId || !previous ? {} : { fontPairId: STYLE_PALETTE_PREFIX + previous.id }),
    ...(recipe.headings || !previous ? {} : { headings: previous.headings }),
  }
}

/**
 * Lo que distingue a un estilo en este paso: la forma de sus botones y de sus
 * tarjetas. Dos estilos con la misma forma se ven iguales aquí (cambian en
 * cosas que la muestra no enseña), así que se ofrece uno solo.
 */
const shapeKey = (style: DesignStyle) => [
  style.button.shape, style.button.shadow, style.button.inner, style.button.size,
  style.card.shape, style.card.shadow, style.card.inner,
].join('|')

/** Un valor del tema con sus `var(--wb-…)` cambiadas por el color de verdad. */
function resolveVars(value: string, theme: ProjectTheme): string {
  return (theme.colorComponents ?? []).reduce((next, token) => next.replaceAll(`var(${token.cssVar})`, token.value), value)
}

/** Los estilos de un botón o una tarjeta del tema, listos para `style`. */
function cssOf(styles: Record<string, unknown> | undefined, theme: ProjectTheme): CSSProperties {
  const out: Record<string, string> = {}
  for (const [key, value] of Object.entries(styles ?? {})) {
    if (typeof value === 'string' && value !== '') out[key] = resolveVars(value, theme)
    else if (typeof value === 'number') out[key] = String(value)
  }
  return out as CSSProperties
}

/** La muestra de la letra: letras sueltas, igual en cualquier idioma. */
const TYPE_SAMPLE = 'Aa Bb Cc'

/** La letra de los títulos del tema, con su respaldo. */
function headingFontOf(theme: ProjectTheme): string | undefined {
  const family = theme.typography?.headingFont
  if (!family) return undefined
  return family.includes(',') ? family : `'${family.replace(/'/g, '')}', sans-serif`
}

/** El ancho al que se dibuja la muestra; luego se escala al de la tarjeta. */
const STAGE_WIDTH = 480

function useStageScale() {
  const ref = useRef<HTMLSpanElement>(null)
  const [scale, setScale] = useState(0.5)
  useEffect(() => {
    const el = ref.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(([entry]) => {
      const width = entry?.contentRect.width ?? 0
      if (width) setScale(width / STAGE_WIDTH)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  return { ref, scale }
}

/**
 * Los botones y dos tarjetas tal como los deja el estilo: con su esquina, su
 * borde, su sombra y su relleno de verdad, sacados del tema que quedaría al
 * elegirlo. Sin letra ni titulares: aquí sólo cambia la forma, y los colores y
 * la letra son los que ya se eligieron.
 */
function LookMock({ theme, withTitle = false }: { theme: ProjectTheme; withTitle?: boolean }) {
  const { ref, scale } = useStageScale()
  const colors = theme.colors
  const button = (id: string, role: 'buttonPrimary' | 'buttonSecondary') => ({
    ...cssOf(theme.roles[role] as Record<string, unknown> | undefined, theme),
    ...cssOf(theme.buttonComponents.find((item) => item.id === id)?.styles as Record<string, unknown> | undefined, theme),
  })
  const card = cssOf(theme.cardComponents.find((item) => item.id === 'card-default')?.styles as Record<string, unknown> | undefined, theme)
  const bar = <i className="cwa-wz-look-label" aria-hidden="true" />
  return (
    <span className={`cwa-wz-look${withTitle ? ' has-title' : ''}`} ref={ref} style={{ background: colors.background }} aria-hidden="true">
      <span className="cwa-wz-look-stage" style={{ width: STAGE_WIDTH, transform: `scale(${scale})`, color: colors.text }}>
        {/* En las combinaciones también la letra: es parte de lo que se elige. */}
        {withTitle ? <span className="cwa-wz-look-title" style={{ fontFamily: headingFontOf(theme) }}>{TYPE_SAMPLE}</span> : null}
        <span className="cwa-wz-look-buttons">
          <span className="cwa-wz-look-button" style={button('button-primary', 'buttonPrimary')}>{bar}</span>
          <span className="cwa-wz-look-button" style={button('button-secondary', 'buttonSecondary')}>{bar}</span>
        </span>
        <span className="cwa-wz-look-cards">
          {[0, 1].map((index) => (
            <span key={index} className="cwa-wz-look-card" style={card}>
              <i className="cwa-wz-look-media" style={{ background: colors.backgroundSecondary }} />
              <i className="cwa-wz-look-line" />
              <i className="cwa-wz-look-line is-short" />
            </span>
          ))}
        </span>
      </span>
    </span>
  )
}

export function StylePresetsStep({ recipe, theme, onChange, aiStyleId, comboStyleId }: StyleStepProps & {
  /** El estilo que eligió la IA y el de la combinación: van primero, con su pastilla. */
  aiStyleId?: string | undefined
  comboStyleId?: string | undefined
}) {
  const { t } = useI18n()
  /* Uno por forma: el elegido representa a su grupo si lo hay. El de la IA y
     el de la combinación, siempre, y delante. */
  const styles = useMemo(() => {
    const pinned = [aiStyleId, comboStyleId].filter((id, at, all): id is string => Boolean(id) && all.indexOf(id) === at)
    const groups = new Map<string, DesignStyle>()
    for (const style of DESIGN_STYLES) {
      if (pinned.includes(style.id)) continue
      const key = shapeKey(style)
      if (!groups.has(key) || style.id === recipe.designStyleId) groups.set(key, style)
    }
    const first = pinned.map((id) => DESIGN_STYLES.find((style) => style.id === id)).filter((style): style is DesignStyle => Boolean(style))
    return [...first, ...[...groups.values()].filter((style) => !first.some((item) => shapeKey(item) === shapeKey(style)) || style.id === recipe.designStyleId)]
  }, [recipe.designStyleId, aiStyleId, comboStyleId])
  /* El tema que dejaría cada estilo: es de ahí de donde salen sus botones y
     tarjetas, así que la muestra enseña exactamente lo que se va a crear. */
  const looks = useMemo(
    () => new Map(styles.map((style) => [style.id, applyStyleRecipe(theme, withStyle(recipe, style.id))])),
    [styles, theme, recipe],
  )
  return (
    <SliderRow className="cwa-wz-styles">
      {styles.map((style) => {
        const active = recipe.designStyleId === style.id
        return (
          <button
            key={style.id}
            type="button"
            className={`cwa-wz-style${active ? ' is-active' : ''}`}
            aria-pressed={active}
            onClick={() => onChange(withStyle(recipe, style.id))}
          >
            <LookMock theme={looks.get(style.id) ?? theme} />
            <span className="cwa-wz-style-copy">
              {style.id === aiStyleId ? <span className="cwa-look-badge"><VelticaMascot outline size={14} />{t('cwa.combination.ai')}</span> : null}
              {style.id === comboStyleId && style.id !== aiStyleId ? <span className="cwa-look-badge"><IconColorSwatch size={12} aria-hidden="true" />{t('cwa.styleOfCombination')}</span> : null}
              <strong>{t(`cwa.look.${style.id}`)}{active ? <IconCheck size={14} aria-hidden="true" /> : null}</strong>
            </span>
          </button>
        )
      })}
    </SliderRow>
  )
}

// ── 2. Los colores ────────────────────────────────────────────────────────────

const PALETTE_GROUPS = PALETTE_GROUP_IDS

/**
 * Los colores de «Tu paleta» que se enseñan y se editan. Las líneas también:
 * son las de la barra, los bordes y los separadores, y un color que se ve en
 * el sitio y no en la fila no se puede encontrar ni cambiar. La superficie
 * sigue saliendo del fondo y el texto.
 */
const BRAND_ROLES = ['background', 'alternate', 'card', 'ink', 'muted', 'button', 'accent', 'border'] as const
type BrandRole = (typeof BRAND_ROLES)[number]
/** Dónde vive cada papel en el tema, para enseñar el que se derivó al no estar fijado. */
const ROLE_IN_THEME: Record<BrandRole, keyof ProjectTheme['colors']> = {
  background: 'background', alternate: 'backgroundSecondary', card: 'card', ink: 'text', muted: 'muted', button: 'primary', accent: 'accent', border: 'border',
}

/** El nombre de una paleta a partir de su id (`pearl-blue` → «Pearl Blue»): ya es una palabra, y es el mismo que enseña el panel de veltica.com. */
const paletteName = (id: string) => id.replace(/[-_]+/g, ' ').replace(/\b[a-z]/g, (letter) => letter.toUpperCase())

/**
 * Una paleta: sus seis colores en una franja y su nombre debajo, como en el
 * panel de diseño de veltica.com. Era una página en miniatura con una tira de
 * colores fina: en tres columnas no se distinguía una paleta de otra.
 */
function PaletteCard({ palette, active, label, name, onClick, caption }: {
  palette: DesignPalette
  active: boolean
  label: string
  /** El nombre que se lee bajo la franja. Sin él (ni `caption`), sólo la franja. */
  name?: string | undefined
  onClick: () => void
  /** De dónde sale la paleta («Creada por la IA»…), en su pastilla bajo la muestra, como en «Combinación». */
  caption?: { badge: string; icon: ReactNode; title?: string | undefined } | undefined
}) {
  return (
    <button type="button" className={`cwa-wz-palette is-stripes${active ? ' is-active' : ''}`} aria-pressed={active} aria-label={label} onClick={onClick}>
      <span className="cwa-wz-palette-stripes">
        {[palette.background, palette.alternate, palette.card, palette.ink, palette.button, palette.accent].map((swatch, at) => <span key={at} style={{ background: swatch }} />)}
      </span>
      <span className="cwa-wz-palette-copy">
        {caption ? <span className="cwa-look-badge">{caption.icon}{caption.badge}</span> : null}
        {caption?.title || name ? <strong>{caption?.title ?? name}</strong> : null}
      </span>
      {active ? <span className="cwa-wz-palette-check"><IconCheck size={11} /></span> : null}
    </button>
  )
}

/** Una paleta de la lista como colores de «Tu paleta»: todos sus papeles. */
function paletteToBrand(palette: Partial<Record<string, unknown>>): CustomPalette {
  const out: CustomPalette = {}
  for (const role of CUSTOM_PALETTE_ROLES) {
    const value = palette[role]
    if (typeof value === 'string' && value) out[role] = value.toUpperCase()
  }
  return out
}

/** «Tu paleta» como `DesignPalette`, para dibujar su tarjeta con `PaletteCard`. */
function brandAsPalette(brand: CustomPalette, theme: ProjectTheme): DesignPalette {
  const pick = (role: BrandRole) => brand[role] ?? theme.colors[ROLE_IN_THEME[role]] ?? '#FFFFFF'
  return {
    background: pick('background'), alternate: pick('alternate'), surface: brand.surface ?? theme.colors.surface ?? pick('alternate'),
    card: pick('card'), border: pick('border'), ink: pick('ink'), button: pick('button'), accent: pick('accent'),
  }
}

/**
 * Los colores: «Tu paleta» arriba y, debajo, paletas de donde partir.
 *
 * «Tu paleta» son SIEMPRE los colores que usará el sitio, cada uno con su papel;
 * pulsar uno abre el selector y lo cambia sólo a él. Abajo, la primera fila es
 * la que armó la IA («Creada por la IA») y después las demás, sin grupos.
 * Elegir cualquiera pone sus colores arriba —ya no es «usar ésta tal cual y
 * dejar la tuya aparte»—, así que se parte de una paleta y se ajusta encima.
 * La de la IA queda guardada aparte: se vuelve a ella con un clic aunque se
 * hayan editado los colores.
 */
export function ColorsStep({ recipe, theme, brand, onBrandChange, aiPalette, aiPaletteId, preset }: StyleStepProps & {
  brand: CustomPalette
  /** Con `paletteId`, se eligió una paleta entera; sin él, se editó un color. */
  onBrandChange: (next: CustomPalette, paletteId?: string) => void
  /** La paleta de la IA; null si el sitio sale de una plantilla. */
  aiPalette: CustomPalette | null
  aiPaletteId: string | null
  /**
   * La paleta del estilo elegido en «Combinación» tal como viene, para volver
   * a ella si se arrepiente de lo cambiado. Null si es la misma que la de la IA.
   */
  preset?: { palette: CustomPalette; id: string; name: string } | null | undefined
}) {
  const { t } = useI18n()
  const selectedId = recipe.paletteId
  const aiActive = Boolean(aiPalette) && selectedId === (aiPaletteId ?? undefined)
  /* El color que se está editando y la muestra de la que cuelga el selector. */
  const [editing, setEditing] = useState<{ role: BrandRole; anchor: HTMLElement } | null>(null)
  const [brandOpen, setBrandOpen] = useState(false)

  /* Al cambiar el fondo o el texto, el borde y la superficie se vuelven a
     derivar de ellos: los de antes eran para otro fondo. */
  const editRole = (role: BrandRole, value: string) => {
    const next: CustomPalette = { ...brand, [role]: value.toUpperCase() }
    if (role === 'background' || role === 'ink') {
      delete next.border
      delete next.surface
      delete next.muted
    }
    onBrandChange(next)
  }

  /* Las paletas de la lista una sola vez —una puede estar en dos grupos— y sin
     la que eligió la IA, que ya va arriba en su fila. */
  const palettes = useMemo(() => {
    const seen = new Set<string>()
    return PALETTE_GROUPS.flatMap((group) => COLOR_PALETTES.filter((palette) => palette.tags.includes(group)))
      .filter((palette) => {
        if (seen.has(palette.id) || palette.id === aiPaletteId || palette.id === preset?.id) return false
        seen.add(palette.id)
        return true
      })
  }, [aiPaletteId, preset?.id])

  return (
    <>
      <Block title={t('cwa.paletteYours')} hint={t('cwa.paletteYoursHint')}>
        {/* En el celular, plegada: una fila con sus colores que al tocarla abre
            la paleta entera para cambiar cada uno (en escritorio no se ve). */}
        <button type="button" className="cwa-wz-brand-summary" aria-expanded={brandOpen} onClick={() => setBrandOpen((open) => !open)}>
          <span className="cwa-wz-brand-dots" aria-hidden="true">
            {BRAND_ROLES.map((role) => <i key={role} style={{ background: brand[role] ?? theme.colors[ROLE_IN_THEME[role]] ?? '#FFFFFF' }} />)}
          </span>
          <span>{t(brandOpen ? 'cwa.paletteHide' : 'cwa.paletteEdit')}</span>
        </button>
        <div className={`cwa-wz-brand${brandOpen ? ' is-open' : ''}`}>
          <div className="cwa-wz-brand-swatches">
            {BRAND_ROLES.map((role) => {
              const value = brand[role] ?? theme.colors[ROLE_IN_THEME[role]] ?? '#FFFFFF'
              return (
                /* El selector del editor (cuadro, tono y HEX) y no el del
                   sistema: en macOS abría la rejilla de Apple sin campo para
                   escribir el código. Ver `ColorPickerPopover`. */
                <button
                  key={role}
                  type="button"
                  className={`cwa-wz-brand-swatch${editing?.role === role ? ' is-editing' : ''}`}
                  aria-label={t('cwa.role.edit', { role: t(`cwa.role.${role}`) })}
                  aria-expanded={editing?.role === role}
                  onClick={(event) => {
                    const anchor = event.currentTarget
                    setEditing((current) => (current?.role === role ? null : { role, anchor }))
                  }}
                >
                  <span className="cwa-wz-brand-chip" style={{ background: value }} />
                  <span className="cwa-wz-brand-copy">
                    <strong>{t(`cwa.role.${role}`)}</strong>
                    <small>{value.toUpperCase()}</small>
                  </span>
                </button>
              )
            })}
          </div>
        </div>
        {editing ? (
          <ColorPickerPopover
            anchor={editing.anchor}
            value={brand[editing.role] ?? theme.colors[ROLE_IN_THEME[editing.role]] ?? '#FFFFFF'}
            label={t('cwa.role.edit', { role: t(`cwa.role.${editing.role}`) })}
            onChange={(hex) => editRole(editing.role, hex)}
            onClose={() => setEditing(null)}
          />
        ) : null}
      </Block>

      <Block title={t('cwa.palettes')} hint={t('cwa.palettesHint')}>
        {/* Las sugeridas y las demás: en el celular, una sola fila que se desliza. */}
        <SliderRow className="cwa-wz-palette-row">
        {aiPalette || preset ? (
          <div className="cwa-wz-palette-group">
            <div className="cwa-wz-palettes">
              {aiPalette ? (
                <PaletteCard
                  palette={brandAsPalette(aiPalette, theme)}
                  active={aiActive}
                  label={t('cwa.paletteByAi')}
                  caption={{ badge: t('cwa.paletteByAi'), icon: <VelticaMascot outline size={14} /> }}
                  onClick={() => onBrandChange({ ...aiPalette }, aiPaletteId ?? undefined)}
                />
              ) : null}
              {preset ? (
                <PaletteCard
                  palette={brandAsPalette(preset.palette, theme)}
                  active={selectedId === preset.id}
                  label={t('cwa.paletteOfStyle')}
                  caption={{ badge: t('cwa.paletteOfStyle'), icon: <IconColorSwatch size={12} aria-hidden="true" />, title: preset.name }}
                  onClick={() => onBrandChange({ ...preset.palette }, preset.id)}
                />
              ) : null}
            </div>
          </div>
        ) : null}
        <div className="cwa-wz-palette-group">
          {aiPalette || preset ? <small>{t('cwa.paletteMore')}</small> : null}
          <div className="cwa-wz-palettes">
            {palettes.map((palette, index) => (
              <PaletteCard
                key={palette.id}
                palette={palette}
                active={selectedId === palette.id}
                label={t('panel.paletteN', { n: index + 1 })}
                name={paletteName(palette.id)}
                onClick={() => onBrandChange(paletteToBrand(palette as unknown as Record<string, unknown>), palette.id)}
              />
            ))}
          </div>
        </div>
        </SliderRow>
      </Block>
    </>
  )
}

// ── 3. La letra ───────────────────────────────────────────────────────────────

type FontFilter = 'all' | 'sans' | 'serif' | 'script'

/** Las familias de una pareja, sea de la lista o la que trae un estilo (`style:<id>`). */
function fontsOf(fontPairId: string | undefined, designStyleId: string | undefined): { heading: string; body: string; serif?: 'heading' | 'both' | undefined; script?: boolean | undefined } | null {
  if (fontPairId && !fontPairId.startsWith(STYLE_PALETTE_PREFIX)) return FONT_PAIRS.find((pair) => pair.id === fontPairId) ?? null
  const styleId = fontPairId ? fontPairId.slice(STYLE_PALETTE_PREFIX.length) : designStyleId
  return DESIGN_STYLES.find((style) => style.id === styleId)?.fonts ?? null
}

/** Una tarjeta de letra con su pastilla de origen («Creada por la IA», «Del estilo»), como en «Combinación». */
function FontOriginCard({ fonts, active, badge, icon, onClick }: {
  fonts: { heading: string; body: string; serif?: 'heading' | 'both' | undefined; script?: boolean | undefined }
  active: boolean
  badge: string
  icon: ReactNode
  onClick: () => void
}) {
  const { t } = useI18n()
  return (
    <button type="button" className={`cwa-wz-font has-badge${active ? ' is-active' : ''}`} aria-pressed={active} onClick={onClick}>
      <span className="cwa-wz-font-sample" style={{ fontFamily: fontStack(fonts.heading, Boolean(fonts.serif), Boolean(fonts.script)) }}>Aa</span>
      <span className="cwa-wz-font-copy">
        <span className="cwa-look-badge">{icon}{badge}</span>
        <strong style={{ fontFamily: fontStack(fonts.heading, Boolean(fonts.serif), Boolean(fonts.script)) }}>{fonts.heading}</strong>
        <small style={{ fontFamily: fontStack(fonts.body, fonts.serif === 'both') }}>
          {fonts.heading === fonts.body ? t('cwa.fontSingle') : t('cwa.fontSample', { font: fonts.body })}
        </small>
      </span>
      {active ? <IconCheck className="cwa-wz-font-check" size={15} aria-hidden="true" /> : null}
    </button>
  )
}

export function FontsStep({ recipe, onChange, aiRecipe }: StyleStepProps & {
  /** La receta de la IA, para su tarjeta; sin ella (template), sólo la del estilo. */
  aiRecipe?: StyleRecipe | undefined
}) {
  const { t } = useI18n()
  const set = usePatch(recipe, onChange)
  const [filter, setFilter] = useState<FontFilter>('all')
  useWizardFonts()
  /* Las cursivas aparte: se eligen por el título, aunque su cuerpo sea serif. */
  const pairs = FONT_PAIRS.filter((pair) => filter === 'all'
    || (filter === 'script' ? Boolean(pair.script) : !pair.script && (filter === 'serif' ? Boolean(pair.serif) : !pair.serif)))
  /* La de la IA, fijada a su estilo si venía «del estilo»: así sigue siendo
     la suya aunque después se elija otra combinación o estilo. */
  const aiFontId = aiRecipe ? (aiRecipe.fontPairId ?? (aiRecipe.designStyleId ? STYLE_PALETTE_PREFIX + aiRecipe.designStyleId : undefined)) : undefined
  const aiFonts = aiFontId ? fontsOf(aiFontId, aiRecipe?.designStyleId) : null
  const styleFonts = fontsOf(undefined, recipe.designStyleId)
  const aiActive = Boolean(aiFontId) && recipe.fontPairId === aiFontId
  const styleActive = !aiActive && (!recipe.fontPairId || recipe.fontPairId === STYLE_PALETTE_PREFIX + recipe.designStyleId)
  return (
    <>
      <div className="cwa-wz-filter" role="tablist">
        {(['all', 'sans', 'serif', 'script'] as const).map((id) => (
          <button key={id} type="button" role="tab" aria-selected={filter === id} className={filter === id ? 'is-active' : ''} onClick={() => setFilter(id)}>
            {t(`cwa.fontFilter.${id}`)}
          </button>
        ))}
      </div>
      {/* La de la IA, la del estilo y las demás: en el celular, una sola fila. */}
      <SliderRow className="cwa-wz-font-strip">
      <div className="cwa-wz-fonts is-origins">
        {aiFonts && aiFontId ? (
          <FontOriginCard fonts={aiFonts} active={aiActive} badge={t('cwa.fontByAi')} icon={<VelticaMascot outline size={14} />} onClick={() => set({ fontPairId: aiFontId })} />
        ) : null}
        {styleFonts ? (
          <FontOriginCard fonts={styleFonts} active={styleActive} badge={t('cwa.fontOfStyle')} icon={<IconColorSwatch size={12} aria-hidden="true" />} onClick={() => set({ fontPairId: undefined })} />
        ) : null}
      </div>
      <div className="cwa-wz-fonts">
        {pairs.map((pair) => {
          const active = recipe.fontPairId === pair.id
          return (
            <button key={pair.id} type="button" className={`cwa-wz-font${active ? ' is-active' : ''}`} aria-pressed={active} onClick={() => set({ fontPairId: pair.id })}>
              <span className="cwa-wz-font-sample" style={{ fontFamily: fontStack(pair.heading, Boolean(pair.serif), Boolean(pair.script)) }}>Aa</span>
              <span className="cwa-wz-font-copy">
                <strong style={{ fontFamily: fontStack(pair.heading, Boolean(pair.serif), Boolean(pair.script)) }}>{pair.heading}</strong>
                <small style={{ fontFamily: fontStack(pair.body, pair.serif === 'both') }}>
                  {pair.heading === pair.body ? t('cwa.fontSingle') : t('cwa.fontSample', { font: pair.body })}
                </small>
              </span>
              {active ? <IconCheck className="cwa-wz-font-check" size={15} aria-hidden="true" /> : null}
            </button>
          )
        })}
      </div>
      </SliderRow>
    </>
  )
}

// ── 0. La combinación ─────────────────────────────────────────────────────────

/** La receta de un look entero: su estilo con su paleta y su letra, sin nada encima. */
export function comboRecipe(styleId: string): StyleRecipe {
  return { designStyleId: styleId, paletteId: STYLE_PALETTE_PREFIX + styleId }
}

/**
 * El primer paso del diseño: combinaciones enteras —estilo, paleta y letra—
 * para elegir por cómo se ve todo junto. La primera es la que eligió la IA; las
 * demás, TODOS los looks con su paleta y su letra. La elegida es el punto de
 * partida: los pasos siguientes (colores, letra, estilo) la van ajustando.
 */
export function CombinationStep({ value, aiTheme, aiStyleId, baseTheme, onChange, origin }: {
  /**
   * Los textos de la primera tarjeta cuando no la eligió la IA (la demo: el
   * diseño con el que abre la página). Sin ellos, los de la IA.
   */
  origin?: { badge: string; hint: string } | undefined
  /** El id del look elegido; `null` = el de la IA. */
  value: string | null
  /** El tema que dejó la IA, para su tarjeta. */
  aiTheme: ProjectTheme
  aiStyleId?: string | undefined
  /** El tema sobre el que se aplica cada look, como al elegirlo (`comboRecipe`). */
  baseTheme: ProjectTheme
  onChange: (styleId: string | null) => void
}) {
  const { t } = useI18n()
  useWizardFonts()
  const looks = useMemo(
    () => new Map(DESIGN_STYLES.map((style) => [style.id, applyStyleRecipe(baseTheme, comboRecipe(style.id))])),
    [baseTheme],
  )
  const aiStyle = DESIGN_STYLES.find((style) => style.id === aiStyleId)
  return (
    <SliderRow className="cwa-looks">
      <button type="button" className={`cwa-look is-ai${value ? '' : ' is-active'}`} aria-pressed={!value} onClick={() => onChange(null)}>
        <LookMock theme={aiTheme} withTitle />
        <span className="cwa-look-copy">
          <span className="cwa-look-badge"><VelticaMascot outline size={14} />{origin?.badge ?? t('cwa.combination.ai')}</span>
          <strong>{aiStyle ? t(aiStyle.labelKey) : t('cwa.combination.aiName')}{value ? null : <IconCheck size={14} aria-hidden="true" />}</strong>
          <small>{origin?.hint ?? t('cwa.combination.aiHint')}</small>
        </span>
      </button>
      {DESIGN_STYLES.map((style) => {
        const active = value === style.id
        return (
          <button key={style.id} type="button" className={`cwa-look${active ? ' is-active' : ''}`} aria-pressed={active} onClick={() => onChange(style.id)}>
            <LookMock theme={looks.get(style.id) ?? aiTheme} withTitle />
            <span className="cwa-look-copy">
              <strong>{t(style.labelKey)}{active ? <IconCheck size={14} aria-hidden="true" /> : null}</strong>
              <small>{t(style.descKey)}</small>
            </span>
          </button>
        )
      })}
    </SliderRow>
  )
}

// ── 4. La animación ───────────────────────────────────────────────────────────

/* Lo que se mueve en la miniatura, en el orden en que entra en el sitio:
   título, texto y botón. Tres piezas bastan para ver el gesto en un icono. */
const MOTION_PARTS = ['title', 'line', 'button'] as const

/** Cada cuánto se repite la muestra mientras el puntero está encima: lo más largo (1200 ms y la cadena) y un respiro. */
const MOTION_LOOP_MS = 1900

/**
 * Los estilos de movimiento de Marca › Animación, tal cual: aquí sólo se elige
 * uno, sin ajustes (se personaliza después en el editor). Cada tarjeta lo
 * reproduce con los colores del sitio en una miniatura: al abrir el paso, en
 * bucle mientras el puntero está encima y al elegirla. La que va con el estilo del sitio sale marcada «Recomendada».
 */
export function MotionStep({ value, suggested, theme, onChange }: {
  value: SiteMotionStyle
  suggested: SiteMotionStyle
  theme: ProjectTheme
  onChange: (style: SiteMotionStyle) => void
}) {
  /* La recomendada primero, a la izquierda; quietos al final: es la opción
     de no animar, no un estilo más. */
  const styles = [suggested, ...SITE_MOTION_STYLES.filter((style) => style !== 'none' && style !== suggested), ...(suggested === 'none' ? [] : ['none' as const])]
  return (
    <SliderRow className="cwa-motions">
      {styles.map((style) => (
        <MotionCard
          key={style}
          style={style}
          theme={theme}
          active={value === style}
          suggested={suggested === style}
          onPick={() => onChange(style)}
        />
      ))}
    </SliderRow>
  )
}

function MotionCard({ style, theme, active, suggested, onPick }: { style: SiteMotionStyle; theme: ProjectTheme; active: boolean; suggested: boolean; onPick: () => void }) {
  const { t } = useI18n()
  /* Cambiar la clave vuelve a montar la muestra, y con ella la animación. */
  const [take, setTake] = useState(0)
  const loop = useRef<number | null>(null)
  const still = () => style === 'none' || Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches)
  const replay = () => { if (!still()) setTake((value) => value + 1) }
  /* Encima, en bucle: una sola pasada acababa en un dibujo quieto, y la
     tarjeta no parecía de animación. */
  const stopLoop = () => {
    if (loop.current !== null) window.clearInterval(loop.current)
    loop.current = null
  }
  const startLoop = () => {
    if (still() || loop.current !== null) return
    replay()
    loop.current = window.setInterval(replay, MOTION_LOOP_MS)
  }
  useEffect(() => stopLoop, [])
  const colors = theme.colors
  return (
    <button
      type="button"
      className={`cwa-motion${active ? ' is-active' : ''}`}
      aria-pressed={active}
      onPointerEnter={(event) => { if (event.pointerType === 'mouse') startLoop() }}
      onPointerLeave={stopLoop}
      onFocus={replay}
      onClick={() => { onPick(); replay() }}
    >
      <span className="cwa-motion-stage" style={{ background: colors.background, color: colors.text }} aria-hidden="true">
        <span key={take} className="cwa-motion-demo" data-motion={style}>
          {MOTION_PARTS.map((part, index) => (
            <i
              key={index}
              className={`cwa-motion-part is-${part}`}
              style={{ '--i': index, ...(part === 'button' ? { background: colors.primary } : {}) } as CSSProperties}
            />
          ))}
        </span>
      </span>
      <span className="cwa-look-copy">
        <strong>
          {t(`motion.style.${style}`)}
          {active ? <IconCheck size={14} aria-hidden="true" /> : null}
          {suggested ? <span className="cwa-look-badge"><VelticaMascot outline size={13} />{t('cwa.motion.suggested')}</span> : null}
        </strong>
        <small>{t(`motion.styleHint.${style}`)}</small>
      </span>
    </button>
  )
}

/**
 * Una fila de opciones que, en la hoja del celular, se desliza de lado
 * (2026-10-30, como el panel de veltica.com): flechas ‹ › a los lados, que se
 * apagan en los extremos, y arrastre con el ratón —con el dedo ya lo hace el
 * navegador—. En escritorio la fila es una rejilla: no hay nada que deslizar y
 * las flechas no se ven (ver `.cwa-slider` en el CSS).
 */
export function SliderRow({ className, children }: { className: string; children: ReactNode }) {
  const { t } = useI18n()
  const ref = useRef<HTMLDivElement>(null)
  const [edges, setEdges] = useState({ start: true, end: true })
  const drag = useRef<{ x: number; left: number; moved: boolean } | null>(null)
  const swallow = useRef(false)
  const sync = () => {
    const row = ref.current
    if (!row) return
    const max = row.scrollWidth - row.clientWidth
    setEdges({ start: row.scrollLeft <= 2, end: row.scrollLeft >= max - 2 })
  }
  useEffect(() => {
    const row = ref.current
    if (!row) return
    sync()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(sync)
    observer.observe(row)
    return () => observer.disconnect()
  }, [])
  const step = (dir: 1 | -1) => ref.current?.scrollBy({ left: dir * Math.max(160, ref.current.clientWidth * 0.75), behavior: 'smooth' })
  return (
    <div className="cwa-slider">
      <button type="button" className="cwa-slider-arrow is-prev" aria-label={t('cwa.slider.prev')} disabled={edges.start} onClick={() => step(-1)}>
        <IconChevronLeft size={16} aria-hidden="true" />
      </button>
      <div
        ref={ref}
        className={className}
        onScroll={sync}
        onPointerDown={(event) => {
          const row = ref.current
          if (event.pointerType !== 'mouse' || event.button !== 0 || !row || row.scrollWidth <= row.clientWidth + 1) return
          drag.current = { x: event.clientX, left: row.scrollLeft, moved: false }
        }}
        onPointerMove={(event) => {
          const current = drag.current
          const row = ref.current
          if (!current || !row) return
          const dx = event.clientX - current.x
          if (!current.moved) {
            if (Math.abs(dx) < 5) return
            current.moved = true
            row.setPointerCapture(event.pointerId)
            row.classList.add('is-dragging')
          }
          row.scrollLeft = current.left - dx
        }}
        onPointerUp={() => {
          const row = ref.current
          if (drag.current?.moved) {
            swallow.current = true
            window.setTimeout(() => { swallow.current = false }, 60)
          }
          drag.current = null
          row?.classList.remove('is-dragging')
        }}
        onClickCapture={(event) => {
          if (!swallow.current) return
          event.preventDefault()
          event.stopPropagation()
        }}
      >
        {children}
      </div>
      <button type="button" className="cwa-slider-arrow is-next" aria-label={t('cwa.slider.next')} disabled={edges.end} onClick={() => step(1)}>
        <IconChevronRight size={16} aria-hidden="true" />
      </button>
    </div>
  )
}
